import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import Method from "@core-ui/ApiServices/Types/Method";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import useChatStore from "@ext/agent/components/store/ChatStore";
import { agentConfig } from "@ext/agent/core/agentConfig";
import { AgentAttachmentStore, type StoredAttachment } from "@ext/agent/core/attachmentStore";
import t from "@ext/localization/locale/translate";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef, useState } from "react";

const getAttachmentError = (file: File): string | null => {
	try {
		AgentAttachmentStore.assertAttachmentExtension(file.name);
	} catch {
		return t("agent.input.file-invalid-format");
	}
	return null;
};

const uploadAttachment = async (
	apiUrlCreator: ApiUrlCreator,
	sessionId: string,
	file: File,
): Promise<StoredAttachment | null> => {
	const url = apiUrlCreator.getAgentSessionUploadAttachmentUrl(
		sessionId,
		file.name,
		file.type || "application/octet-stream",
	);
	try {
		const data = Buffer.from(await file.arrayBuffer());
		const res = await FetchService.fetch(url, { data } as unknown as BodyInit, MimeTypes.text, Method.POST, false);
		if (!res.ok) return null;
		return await res.json();
	} catch {
		return null;
	}
};

const deleteAttachment = (apiUrlCreator: ApiUrlCreator, sessionId: string, attachment: StoredAttachment) =>
	void FetchService.fetch(apiUrlCreator.getAgentSessionDeleteAttachmentUrl(sessionId, attachment.storagePath));

export const useAttachFiles = (
	attachments: StoredAttachment[],
	onAttachmentsChange: Dispatch<SetStateAction<StoredAttachment[]>>,
) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [fileError, setFileError] = useState<string | null>(null);
	const pendingCountRef = useRef(0);

	const attachFiles = useCallback(
		(files: File[]) => {
			const errors = files.map(getAttachmentError);
			const valid = files.filter((_, i) => !errors[i]);
			const freeSlots = agentConfig.maxAttachments - attachments.length - pendingCountRef.current;
			const accepted = valid.slice(0, Math.max(freeSlots, 0));
			const limitError =
				accepted.length < valid.length
					? t("agent.input.too-many-files").replace("{{max}}", String(agentConfig.maxAttachments))
					: null;
			setFileError(errors.find(Boolean) ?? limitError);
			const sessionId = useChatStore.getState().activeSessionId;
			if (!accepted.length || !sessionId) return;

			pendingCountRef.current += accepted.length;
			void Promise.all(accepted.map((file) => uploadAttachment(apiUrlCreator, sessionId, file)))
				.then((uploaded) => {
					const saved = uploaded.filter((attachment): attachment is StoredAttachment => attachment !== null);
					if (saved.length < uploaded.length) setFileError(t("file-upload-failed"));
					if (useChatStore.getState().activeSessionId !== sessionId) {
						for (const attachment of saved) deleteAttachment(apiUrlCreator, sessionId, attachment);
						return;
					}
					onAttachmentsChange((prev) => [...prev, ...saved]);
				})
				.finally(() => {
					pendingCountRef.current -= accepted.length;
				});
		},
		[attachments.length, onAttachmentsChange],
	);

	const detachFile = useCallback(
		(index: number) => {
			const sessionId = useChatStore.getState().activeSessionId;
			const attachment = attachments[index];
			onAttachmentsChange(attachments.filter((_, i) => i !== index));
			if (sessionId) deleteAttachment(apiUrlCreator, sessionId, attachment);
		},
		[attachments, onAttachmentsChange],
	);

	const isAttaching = useCallback(() => pendingCountRef.current > 0, []);

	return useMemo(
		() => ({ attachFiles, detachFile, isAttaching, fileError, setFileError }),
		[attachFiles, detachFile, isAttaching, fileError],
	);
};
export type AttachFiles = ReturnType<typeof useAttachFiles>;
